import { RoughUnderline } from "../../../components/FirstTimeLetter/index.jsx";
import { Word, Correction, GrowthChartCard, QuestionNetworkCard, BotIcon } from "./demos.jsx";
import { motion } from "framer-motion";
import { useState, useRef, useLayoutEffect } from "react";
import { MoveRight } from "lucide-react";

// features


const cardVariant = {
    hidden: { opacity: 0, y: 16, scale: 0.96 },
    show: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.3, ease: "easeOut" },
    },
};

const rule = {
    hidden: { scaleY: 0 },
    show: {
        scaleY: 1,
        transition: { duration: 0.5, ease: "easeOut" },
    },
};
const visualFadeUp = {
    hidden: { opacity: 0, y: 10 },
    show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.2, ease: "easeOut" },
    },
};

const textContainer = {
    hidden: {},
    show: {
        transition: {
            staggerChildren: 0.15,
            delayChildren: 0.1,
        },
    },
};

const fadeUp = {
    hidden: { opacity: 0, y: 10 },
    show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: "easeOut" },
    },
};



export const Feature1 = ({ className, maxVisualWidth = "md:min-w-[350px] lg:min-w-[450px]" }) => {
    const underline = "underline underline-offset-4 decoration-dashed decoration-1 decoration-[#B4A7C3]"
    const outline = "bg-[#70B08E]/40 px-[3px] rounded-md"

    const rootRef = useRef(null);
    const signalRef = useRef(null);
    const [wordPos, setWordPos] = useState(null);

    const updatePosition = () => {
        if (!signalRef.current || !rootRef.current) return;
        const signalRect = signalRef.current.getBoundingClientRect();
        const rootRect = rootRef.current.getBoundingClientRect();
        setWordPos({
            top: signalRect.top - rootRect.top,
            left: signalRect.left - rootRect.left,
        });
    };

    useLayoutEffect(() => {
        updatePosition();
        window.addEventListener("resize", updatePosition);
        return () => window.removeEventListener("resize", updatePosition);
    }, []);

    const wordContainer = {
        hidden: {},
        show: {
            transition: {
                staggerChildren: 0.05,
                delayChildren: 0.1,
            },
        },
    };

    const wordItem = {
        hidden: { opacity: 0, y: 4 },
        show: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.15, ease: "easeOut" },
        },
    };

    return (
        <div ref={rootRef} className={`relative flex flex-col md:flex-row gap-10 p-10 px-7 py-10 ${className}`}>
            {/* visual */}
            <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.4 }}
                variants={cardVariant}
                className={`${maxVisualWidth} flex justify-center items-center`}
            >
                <div className="flex flex-col gap-3 max-w-[300px]">
                    <div className="text-base-text font-light text-[13px]">
                        <p>あなたが</p>
                        <p>知っている752語から、</p>
                        <p>選ばれた文章。</p>
                    </div>

                    <div className="bg-white/50 rounded-2xl shadow-sm p-3 pr-20">
                        <motion.p
                            variants={wordContainer}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, amount: 0.4 }}
                            onAnimationComplete={updatePosition}
                            className="text-[16px] [@media(max-width:355px_and_min-width:330px)]:text-[14px] [@media(max-width:354px)]:text-[13px] max-w-xs flex flex-wrap gap-x-1.5 gap-y-1 text-base text-base-text font-light break-words"
                        >
                            <motion.span variants={wordItem} className={underline}>Bitter</motion.span>
                            <motion.span variants={wordItem} className={underline}>flavors</motion.span>
                            <motion.span variants={wordItem}>often</motion.span>
                            <motion.span ref={signalRef} variants={wordItem} className={outline}>
                                signal
                            </motion.span>
                            <motion.span variants={wordItem}>that</motion.span>
                            <motion.span variants={wordItem}>a</motion.span>
                            <motion.span variants={wordItem} className={underline}>plant</motion.span>
                            <motion.span variants={wordItem}>is</motion.span>
                            <motion.span variants={wordItem} className={underline}>powerful</motion.span>
                            <motion.span variants={wordItem}>or</motion.span>
                            <motion.span variants={wordItem} className={outline}>protective</motion.span>
                            <motion.span variants={wordItem}>.</motion.span>
                        </motion.p>
                    </div>
                </div>
            </motion.div>

            {/* text */}
            <div className="md:flex items-center">
                <motion.div
                    variants={textContainer}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.3 }}
                    className="flex flex-col gap-5 text-white text-[17px] tracking-wide leading-[1.6]"
                >
                    <div className="flex items-stretch gap-2">
                        <motion.div
                            variants={rule}
                            style={{ transformOrigin: "top" }}
                            className="w-px h-auto self-stretch bg-[#B2D396]"
                        />
                        <motion.div variants={fadeUp} className="text-white text-[19px] font-medium">
                            <p>読むものも、観るものも、</p>
                            <p>学習用につくられたものではありません。</p>
                        </motion.div>
                    </div>

                    <motion.p variants={fadeUp}>英語圏で実際に読まれ、観られているもの——誰かが、誰かに伝えたくてつくったものです。</motion.p>
                    <motion.p variants={fadeUp}>aomeは、それを、あなたの語彙に合わせて選びます。</motion.p>
                    <motion.p variants={fadeUp} className="font-medium">レベル分けは、していません。あなたが知っている語を、一語ずつ見ています。</motion.p>
                    <motion.p variants={fadeUp}>知らない言葉が、20%前後。読むのに、力がいります。言葉が根づくのは、その密度です。</motion.p>
                </motion.div>
            </div>

            {wordPos && (
                <motion.div
                    className="absolute z-20 transform-gpu w-max bg-white/50 backdrop-blur rounded-2xl p-2.5 px-3.5 flex flex-col gap-3 shadow-sm"
                    style={{
                        top: wordPos.top,
                        left: wordPos.left - 4,
                    }}
                    initial={{
                        opacity: 0,
                        transform: "translate(-5px, calc(-100% + 4px))"
                    }}
                    whileInView={{
                        opacity: 1,
                        transform: "translate(-5px, calc(-100% - 4px))"
                    }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.3, ease: "easeOut", delay: 1 }}
                >
                    <div className="flex gap-3 items-center">
                        <p className="text-base-text text-[16px]">Signal</p>

                        <div className="flex gap-1 items-center">
                            <span className="rounded-[5px] bg-[#DCA6A6] w-5 h-3" />
                            <span className="rounded-[5px] bg-[#A6BBDC] w-5 h-3" />
                            <span className="rounded-[5px] bg-[#8BC5C3] w-6 h-3" />
                        </div>
                    </div>

                    <div className="font-light text-base-text text-sm pl-2">
                        <p>示す</p>
                        <p>知らせる</p>
                        <p>合図する</p>
                    </div>
                </motion.div>
            )}
        </div>
    )
}

export const Feature2 = ({ className, maxVisualWidth = "md:min-w-[350px] lg:min-w-[450px]" }) => {
    const underline = "underline underline-offset-4 decoration-dashed decoration-1 decoration-[#B4A7C3]"
    const outline = "bg-[#70B08E]/40 px-[3px] rounded-md"

    return (
        <div className={`flex flex-col md:flex-row gap-10 p-10 px-7 py-10 ${className}`}>

            {/* visual */}

            <div className="flex flex-col w-full justify-center">
                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.9 }}
                    variants={cardVariant}
                    className={`${maxVisualWidth} flex justify-center items-center`}
                >
                    <div className="flex flex-col gap-3 items-center w-full">
                        <div className="">
                            <p className="text-left w-[280px] text-[#BAE1E9] text-sm">
                                ……アオメ、signalのあと、いつもthatが来るのかな？ それとも、ここだけ？
                            </p>
                        </div>

                        <div className="bg-white/50 rounded-2xl shadow-sm p-3 relative max-w-[300px]">
                            <p className="text-[16px] max-w-xs flex flex-wrap gap-x-1.5 gap-y-1 text-base text-base-text font-light break-words">
                                <span className={underline}>Bitter</span>
                                <span className={underline}>flavors</span>
                                <span>often</span>
                                <span className={`${outline}`}>signal</span>
                                <span>that</span>
                                <span>a</span>
                                <span className={underline}>plant</span>
                                <span>is</span>
                                ...
                            </p>
                        </div>


                    </div>
                </motion.div>

                {/* Popup element */}
                <div className="flex flex-col items-center w-full -mt-2 ml-4 ">
                    <motion.div
                        // variants={popUpVariant}
                        initial={{
                            opacity: 0,
                            y: -10
                        }}
                        whileInView={{
                            opacity: 1,
                            y: 0
                        }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.3, ease: "easeOut", delay: 0.5 }}
                        className="w-[270px] [@media(max-width:355px)]:max-w-[250px] flex flex-col rounded-2xl bg-white/50 backdrop-blur shadow-sm p-3 transform-gpu"
                    >
                        <div className="flex gap-2 items-start">
                            <BotIcon size={24} className="shrink-0" />
                            <p className="font-light text-[13px] text-base-text">「signal」の後に必ず「that」が来るわけではありませんが、よく使われます。ここでは「…を示す」という動詞で、that節が「何を示すか」を...</p>
                        </div>
                    </motion.div>
                </div>

            </div>

            {/* text */}
            <div className="md:flex items-center">
                <motion.div
                    variants={textContainer}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.3 }}
                    className="flex flex-col gap-6 text-white text-[17px] tracking-wide leading-[1.6]"
                >
                    <div className="flex items-stretch gap-2">
                        <motion.div
                            variants={rule}
                            style={{ transformOrigin: "top" }}
                            className="w-px h-auto self-stretch bg-[#B2D396]"
                        />
                        <motion.div variants={fadeUp} className="text-white text-[19px] font-medium">
                            <p>文章や映像を理解しようとする中で、</p>
                            <p>ふと浮かんだ問い。</p>
                        </motion.div>
                    </div>

                    <motion.p variants={fadeUp}>なぜ、ここでこの言い方なのか。いつもこうなのか、それとも、ここだけなのか。意味はわかる。それでも、何かが引っかかる。</motion.p>
                    <motion.p variants={fadeUp}>その多くは、確かめないまま消えていきます。調べるほどではない、と。</motion.p>
                    <motion.p variants={fadeUp}>アオメは、あなたがいま読んでいるもの、観ているものを知っています。だから、浮かんだその場で、訊けます。問うことが、特別なことではなくなります。</motion.p>
                </motion.div>
            </div>
        </div>
    )
}

// export const Feature3 = ({ className, maxVisualWidth = "md:min-w-[350px] lg:min-w-[450px]" }) => {
//     const rootRef = useRef(null);
//     const underlineRef = useRef(null);
//     const [correctionPos, setCorrectionPos] = useState(null);

//     const updatePosition = () => {
//         if (!underlineRef.current || !rootRef.current) return;
//         const underlineRect = underlineRef.current.getBoundingClientRect();
//         const rootRect = rootRef.current.getBoundingClientRect();
//         setCorrectionPos({
//             top: underlineRect.top - rootRect.top,
//             left: underlineRect.left - rootRect.left,
//         });
//     };

//     useLayoutEffect(() => {
//         updatePosition();
//         window.addEventListener("resize", updatePosition);
//         return () => window.removeEventListener("resize", updatePosition);
//     }, []);

//     return (
//         <div ref={rootRef} className={`relative flex flex-col md:flex-row gap-10 px-5 py-10 ${className}`}>
//             {/* visual */}
//             <motion.div
//                 initial="hidden"
//                 whileInView="show"
//                 viewport={{ once: true, amount: 0.4 }}
//                 variants={cardVariant}
//                 className={`${maxVisualWidth} flex justify-center items-center`}
//             >
//                 <motion.div
//                     initial="hidden"
//                     whileInView="show"
//                     viewport={{ once: true, amount: 0.4 }}
//                     transition={{ staggerChildren: 0.2, delayChildren: 0.1 }}
//                     onAnimationComplete={updatePosition}
//                     className="flex flex-col gap-3 max-w-[305px] pb-[90px]"
//                 >
//                     {/* Top Thought */}
//                     <motion.div variants={visualFadeUp} className="text-white text-sm">
//                         <p>この味、最初は違和感があるけど、</p>
//                         <p>だんだん好きになる。</p>
//                     </motion.div>

//                     {/* Sub thought */}
//                     <motion.p variants={visualFadeUp} className="text-[#BAE1E9] text-xs">
//                         ——そう言いたい。
//                     </motion.p>

//                     {/* Chat Bubble with Avatar */}
//                     <motion.div variants={visualFadeUp} className="flex items-start gap-2">
//                         <div className="rounded-full shrink-0 w-6 h-6 bg-[#A2D6CA]" />
//                         <div className="bg-white/50 rounded-2xl p-3 shadow-sm">
//                             <p className="text-sm max-w-xs flex flex-wrap gap-x-1.5 gap-y-1 text-base text-base-text font-light break-words">
//                                 <span>At first this taste is strange, but</span>
//                                 <span>little by little</span>

//                                 <span ref={underlineRef}>
//                                     <RoughUnderline className="text-base-text" initialDelay={700} isView>
//                                         I become to
//                                     </RoughUnderline>
//                                 </span>

//                                 <span>like it.</span>
//                             </p>
//                         </div>
//                     </motion.div>

//                     {/* Final thought */}
//                     <motion.p variants={visualFadeUp} className="text-[#BAE1E9] text-xs">
//                         ……言えた、と思う。
//                     </motion.p>
//                 </motion.div>
//             </motion.div>

//             {/* text */}
//             <div className="md:flex items-center">
//                 <motion.div
//                     variants={textContainer}
//                     initial="hidden"
//                     whileInView="show"
//                     viewport={{ once: true, amount: 0.3 }}
//                     className="flex flex-col gap-6 text-white text-[17px] tracking-wide leading-[1.6]"
//                 >
//                     <div className="flex items-stretch gap-2">
//                         <motion.div
//                             variants={rule}
//                             style={{ transformOrigin: "top" }}
//                             className="w-px h-auto self-stretch bg-[#B2D396]"
//                         />
//                         <motion.div variants={fadeUp} className="text-white text-[19px] font-medium">
//                             <p>まず、日本語で、完全に感じてください。</p>
//                             <p>それから、その言葉に手を伸ばす。</p>
//                         </motion.div>
//                     </div>

//                     <motion.p variants={fadeUp}>
//                         アオメは、あなたが言いたいことに、耳を傾けます。言葉が出てこなくても、そこで終わりにはしません。言いたかったことと、実際に言えたことの差を、そのまま見せてくれます。
//                     </motion.p>
//                     <motion.p variants={fadeUp}>
//                         アオメの言葉も、あなたの語彙に合わせて返ってきます。ここで出会った言葉も、読むときと同じように、aomeの中に積み重なっていきます。
//                     </motion.p>
//                 </motion.div>
//             </div>

//             {/* Correction — plain sibling, not inside any motion component */}
//             {correctionPos && (
//                 <motion.div
//                     style={{
//                         top: correctionPos.top,
//                         left: correctionPos.left,
//                     }}
//                     initial={{
//                         opacity: 0,
//                         transform: "translate(-16px, -4px)"
//                     }}
//                     whileInView={{
//                         opacity: 1,
//                         transform: "translate(-16px, 4px)"
//                     }}
//                     viewport={{ once: true, amount: 0.3 }}
//                     transition={{ duration: 0.3, ease: "easeOut", delay: 0.9 }}
//                     className="transform-gpu backdrop-blur bg-white/50 rounded-2xl p-3 flex flex-col gap-4 absolute z-20 mt-6 -left-4 max-w-[210px]"
//                 >
//                     <div className="flex justify-between items-center">
//                         <div className="flex gap-2 font-light items-center text-base-text text-xs">
//                             <p className="line-through">I become to</p>
//                             <MoveRight className="text-base-text size-5" strokeWidth={1} />
//                             <p>I started to</p>
//                         </div>
//                     </div>

//                     <div className="flex gap-1 items-start font-light ">
//                         <BotIcon size={22} className="shrink-0" />
//                         <p className="text-xs text-base-text">「好きになる」を become で組み立てたんですね。英語では、その変化を動詞ひとつで言うことが多いです。</p>

//                     </div>
//                 </motion.div>
//             )}
//         </div>
//     )
// }

export const Feature3 = ({ className, maxVisualWidth = "md:min-w-[350px] lg:min-w-[450px]" }) => {
    const rootRef = useRef(null);
    const underlineRef = useRef(null);
    const [correctionPos, setCorrectionPos] = useState(null);

    const updatePosition = () => {
        if (!underlineRef.current || !rootRef.current) return;
        const underlineRect = underlineRef.current.getBoundingClientRect();
        const rootRect = rootRef.current.getBoundingClientRect();
        setCorrectionPos({
            top: underlineRect.top - rootRect.top,
            left: underlineRect.left - rootRect.left,
        });
    };

    useLayoutEffect(() => {
        updatePosition();
        window.addEventListener("resize", updatePosition);
        return () => window.removeEventListener("resize", updatePosition);
    }, []);

    return (
        <div ref={rootRef} className={`relative flex flex-col md:flex-row gap-10 py-10 ${className}`}>
            {/* visual */}
            <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.4 }}
                variants={cardVariant}
                className={`${maxVisualWidth} flex justify-center items-center`}
            >
                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ staggerChildren: 0.2, delayChildren: 0.1 }}
                    onAnimationComplete={updatePosition}
                    className="flex flex-col gap-3 max-w-[90vw] pb-[120px]"
                >

                    {/* Chat Bubble with Avatar */}
                    <motion.div variants={visualFadeUp} className="flex items-start gap-1">
                        {/* <div className="rounded-full shrink-0 w-6 h-6 bg-[#A2D6CA]" /> */}
                        <BotIcon size={24} className="shrink-0" outlineColor="#FFFFFF" />
                        <div className="bg-white/50 rounded-2xl p-3 shadow-sm max-w-[240px]">
                            <p className="text-sm max-w-xs flex flex-wrap gap-x-1.5 gap-y-1 text-base text-base-text font-light break-words">
                                What do you like to have when you feel happy?
                            </p>
                        </div>
                    </motion.div>

                    {/* Final thought */}
                    <motion.div variants={visualFadeUp} className="text-[#BAE1E9] text-xs ml-8 ">
                        <p>うーん、コーヒーかな。</p>
                        <p>最初は苦くて違和感あったけど、だんだん好きになった。</p>
                    </motion.div>

                    <motion.div variants={visualFadeUp} className="flex justify-end gap-1 w-full">
                        <div className="bg-white/50 rounded-2xl p-3 shadow-sm max-w-[240px]">
                            <p className="text-sm max-w-xs text-base text-base-text font-light break-words">
                                <span>I really like to drink coffee. The bitter taste was strange at first but I </span>

                                <RoughUnderline
                                    ref={underlineRef}
                                    className="text-base-text inline"
                                    initialDelay={700}
                                    isView
                                >
                                    became to like
                                </RoughUnderline>

                                <span> it.</span>
                            </p>
                        </div>
                        <div className="rounded-full shrink-0 w-6 h-6 bg-[#A2D6CA]" />
                    </motion.div>
                </motion.div>
            </motion.div>

            {/* text */}
            <div className="md:flex items-center px-5">
                <motion.div
                    variants={textContainer}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.3 }}
                    className="flex flex-col gap-6 text-white text-[17px] tracking-wide leading-[1.6]"
                >
                    <div className="flex items-stretch gap-2">
                        <motion.div
                            variants={rule}
                            style={{ transformOrigin: "top" }}
                            className="w-px h-auto self-stretch bg-[#B2D396]"
                        />
                        <motion.div variants={fadeUp} className="text-white text-[19px] font-medium">
                            <p>まず、日本語で、完全に感じてください。</p>
                            <p>それから、その言葉に手を伸ばす。</p>
                        </motion.div>
                    </div>

                    <motion.p variants={fadeUp}>
                        アオメは、あなたが言いたいことに、耳を傾けます。言葉が出てこなくても、そこで終わりにはしません。言いたかったことと、実際に言えたことの差を、そのまま見せてくれます。
                    </motion.p>
                    <motion.p variants={fadeUp}>
                        アオメの言葉も、あなたの語彙に合わせて返ってきます。ここで出会った言葉も、読むときと同じように、aomeの中に積み重なっていきます。
                    </motion.p>
                </motion.div>
            </div>

            {/* Correction — plain sibling, not inside any motion component */}
            {correctionPos && (
                <motion.div
                    style={{
                        top: correctionPos.top,
                        left: correctionPos.left,
                    }}
                    initial={{
                        opacity: 0,
                        transform: "translate(-16px, -4px)"
                    }}
                    whileInView={{
                        opacity: 1,
                        transform: "translate(-16px, 4px)"
                    }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.3, ease: "easeOut", delay: 0.9 }}
                    className="transform-gpu backdrop-blur bg-white/50 rounded-2xl p-3 flex flex-col gap-4 absolute z-20 mt-6 -left-4 max-w-[220px]"
                >
                    <div className="flex justify-between items-center">
                        <div className="flex gap-2 font-light items-center text-base-text text-xs">
                            <p className="line-through">became to like</p>
                            <MoveRight className="text-base-text size-5" strokeWidth={1} />
                            <p>started to like</p>
                        </div>
                    </div>

                    <div className="flex gap-1 items-start font-light ">
                        <BotIcon size={22} className="shrink-0" />
                        <p className="text-xs text-base-text">「become」は状態そのものが変わることを表します。become tired、become famous のように、後には形容詞や名詞が...</p>

                    </div>
                </motion.div>
            )}
        </div>
    )
}

export const Feature4 = ({ className, maxVisualWidth = "md:min-w-[350px] lg:min-w-[450px]" }) => {
    const underline = "underline underline-offset-4 decoration-dashed decoration-1 decoration-[#B4A7C3]"
    const outline = "bg-[#70B08E]/40 px-[3px] rounded-md"

    return (
        <div className={`flex flex-col md:flex-row gap-10 px-5 py-10 ${className}`}>
            {/* visual */}

            <div className={`${maxVisualWidth} flex justify-center items-center`}>
                <div className="flex flex-col gap-3 items-center">
                    <div className="flex flex-col max-w-[280px] md:max-w-[320px] items-center">
                        <motion.div
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, amount: 0.4 }}
                            variants={cardVariant}
                            className="bg-white/50 rounded-3xl p-3"
                        >
                            <GrowthChartCard />
                        </motion.div>


                        <div className="flex flex-col gap-2 items-center w-[310px] md:w-[350px] -mt-3">
                            <motion.div
                                initial={{
                                    opacity: 0,
                                    y: -10
                                }}
                                whileInView={{
                                    opacity: 1,
                                    y: 0
                                }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.3, ease: "easeOut", delay: 0.5 }}
                                className="bg-white/50 backdrop-blur rounded-2xl p-3 transform-gpu"
                            >
                                <p className="text-[16px] max-w-xs flex flex-wrap gap-x-1.5 gap-y-1 text-base text-base-text font-light break-words">
                                    <span>often</span>
                                    <span className={outline}>
                                        signal
                                    </span>
                                    <span>that</span>
                                    <span>a</span>
                                    <span className={underline}>plant</span>
                                </p>


                            </motion.div>
                            <motion.p
                                initial={{
                                    opacity: 0,
                                    y: -5
                                }}
                                whileInView={{
                                    opacity: 1,
                                    y: 0
                                }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.3, ease: "easeOut", delay: 0.6 }}
                                className="text-[#BAE1E9] text-xs transform-gpu"
                            >
                                ……この言葉、どんな言葉と並ぶんだろう
                            </motion.p>
                        </div>
                    </div>

                </div>
            </div>

            {/* text */}
            <div className="md:flex justify-center items-center">
                <motion.div
                    variants={textContainer}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.1 }}
                    className="flex flex-col gap-6 text-white text-[17px] tracking-wide leading-[1.6]"
                >
                    <div className="flex items-stretch gap-2">
                        <motion.div
                            variants={rule}
                            style={{ transformOrigin: "top" }}
                            className="w-px h-auto self-stretch bg-[#B2D396]"
                        />
                        <motion.div variants={fadeUp} className="text-white text-[19px] font-medium">
                            <p>知らない言葉に出会ったら、</p>
                            <p>その場で立ち止まれます。</p>
                        </motion.div>
                    </div>

                    <motion.p variants={fadeUp}>
                        意味だけでなく、どんな言葉と並び、どんな場面で使われるのか。言葉には、生きている場所があります。aomeは、出会った言葉を、一つも忘れません。そして、あなたが忘れかけた頃に、もう一度、その言葉を差し出します。
                    </motion.p>
                </motion.div>
            </div>
        </div>
    )
}

export const Feature5 = ({ className, maxVisualWidth = "md:min-w-[350px] lg:min-w-[450px]" }) => {

    return (
        <div className={`flex flex-col md:flex-row gap-5 md:gap-10 px-5 py-10 ${className}`}>
            {/* visual */}

            <div className={`${maxVisualWidth} flex justify-center items-center`}>
                <div className="flex flex-col gap-3 items-center pb-[50px]">
                    <div className="max-w-[280px] md:max-w-[320px]">
                        <QuestionNetworkCard count={67} />
                    </div>
                </div>
            </div>

            {/* text */}
            <div className="md:flex items-center">
                <motion.div
                    variants={textContainer}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, amount: 0.1 }}
                    className="flex flex-col gap-6 text-white text-[17px] tracking-wide leading-[1.6]"
                >
                    <div className="flex items-stretch gap-2">
                        <motion.div
                            variants={rule}
                            style={{ transformOrigin: "top" }}
                            className="w-px h-auto self-stretch bg-[#B2D396]"
                        />
                        <motion.div variants={fadeUp} className="text-white text-[19px] font-medium">
                            <p>読むことも、書こうとすることも</p>
                            <p>思い出すこともaomeの中では</p>
                            <p>同じ一つの記憶から生まれています。</p>
                        </motion.div>
                    </div>

                    <motion.p variants={fadeUp}>
                        一つの場所に、すべてが繋がっているからこそ今日出会ったものが、明日出会うものを形づくっていきます。同じ歩き方をする人は、ふたりといません。
                    </motion.p>
                </motion.div>
            </div>
        </div>
    )
}